import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Pages
import Dashboard from './pages/Dashboard';
import Shop from './pages/Shop';
import Events from './pages/Events';
import SignIn from './pages/SignIn';
import Register from './pages/Register';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminFeedback from './pages/AdminFeedback';
import AdminProducts from './pages/AdminProducts';
import AdminEvents from './pages/AdminEvents';
import ImageLibrary from './pages/ImageLibrary';
import AdminGallery from './pages/AdminGallery';
import AdminCertificates from './pages/AdminCertificates';

// Auth Context
import { AuthProvider, useAuth } from './context/AuthContext';
import VerifyCertificate from './pages/VerifyCertificate';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  useEffect(() => {
    // Orb animation
    let t = 0;
    const animateOrbs = () => {
      t += 0.008;
      const orbs = document.querySelectorAll('.orb');
      orbs.forEach((o, i) => {
        o.style.transform = `translateY(${Math.sin(t * (0.6 + i * 0.4)) * 10}px) translateX(${Math.cos(t * (0.4 + i * 0.6)) * 8}px)`;
      });
      requestAnimationFrame(animateOrbs);
    };
    animateOrbs();
  }, []);

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/events" element={<Events />} />
        <Route path="/VerifyCertificate" element={<VerifyCertificate />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />


        {/* Protected User Routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feedback"
          element={
            <ProtectedRoute adminOnly>
              <AdminFeedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/products"
          element={
            <ProtectedRoute adminOnly>
              <AdminProducts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events"
          element={
            <ProtectedRoute adminOnly>
              <AdminEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/image-library"
          element={
            <ProtectedRoute adminOnly>
              <ImageLibrary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/gallery"
          element={
            <ProtectedRoute adminOnly>
              <AdminGallery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/certificates"
          element={
            <ProtectedRoute adminOnly>
              <AdminCertificates />
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Decorative orbs */}
      <div className="orb o1" aria-hidden="true"></div>
      <div className="orb o2" aria-hidden="true"></div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
